/**
 * Constants for spatial audio behavior.
 */
const MAX_HEARING_DISTANCE = 250;
const PAN_FULL_OFFSET = 120;
const PITCH_VARIATION = 0.1;
const VOLUME_VARIATION = 0.1;
const BASE_VOLUME = 0.3;

/**
 * Client-side service for spatial audio playback using Web Audio API.
 * Supports directional panning and distance-based volume attenuation.
 */
export class SpatialSoundService {
  private context: AudioContext | undefined;
  private bufferCache = new Map<string, AudioBuffer>();
  private loadingPromises = new Map<string, Promise<AudioBuffer>>();

  /**
   * Lazily initialized AudioContext (created on first call to getContext()).
   * Note: this may occur during sound preloading (e.g., via loadSound()),
   * not necessarily at the first user-heard playback. Browsers still require
   * a user gesture to reliably start or resume AudioContext; callers should
   * ensure the context is resumed in response to such a gesture when needed.
   */
  private getContext(): AudioContext {
    if (!this.context) {
      const AudioContextClass =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.context = new AudioContextClass();
    }
    return this.context;
  }

  /**
   * Pre-loads an audio file into the cache.
   * @param path Asset path relative to /assets/
   */
  public async loadSound(path: string): Promise<AudioBuffer> {
    const cached = this.bufferCache.get(path);
    if (cached) return cached;

    const existingPromise = this.loadingPromises.get(path);
    if (existingPromise) return existingPromise;

    const promise = (async () => {
      try {
        const response = await fetch(`/assets/${path}`);
        if (!response.ok) {
          throw new Error(
            `Failed to fetch sound "${path}": ${response.status} ${response.statusText}`,
          );
        }
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await this.getContext().decodeAudioData(arrayBuffer);
        this.bufferCache.set(path, audioBuffer);
        return audioBuffer;
      } catch (error) {
        console.error(`SpatialSoundService: Failed to load sound "${path}":`, error);
        throw error;
      } finally {
        this.loadingPromises.delete(path);
      }
    })();

    this.loadingPromises.set(path, promise);
    return promise;
  }

  /**
   * Plays a sound with spatial characteristics relative to a listener.
   * @param soundPath Asset path relative to /assets/
   * @param sourceX World X of the sound source
   * @param sourceY World Y of the sound source
   * @param listenerX World X of the listener (e.g., local player)
   * @param listenerY World Y of the listener
   */
  public playSpatial(
    soundPath: string,
    sourceX: number,
    sourceY: number,
    listenerX: number,
    listenerY: number,
  ): void {
    const buffer = this.bufferCache.get(soundPath);
    if (!buffer) {
      // Sound not loaded, trigger load but don't wait (for next time)
      void this.loadSound(soundPath);
      return;
    }

    const ctx = this.getContext();
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }

    const dx = sourceX - listenerX;
    const dy = sourceY - listenerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance > MAX_HEARING_DISTANCE) {
      return;
    }

    // Calculate volume falloff (linear)
    let gainValue = Math.max(0, 1 - distance / MAX_HEARING_DISTANCE) * BASE_VOLUME;

    // Calculate stereo panning
    // -1 (full left) to 1 (full right)
    const panValue = Math.max(-1, Math.min(1, dx / PAN_FULL_OFFSET));

    // Apply randomization
    const pitchShift = 1 + (Math.random() * 2 - 1) * PITCH_VARIATION;
    gainValue *= 1 + (Math.random() * 2 - 1) * VOLUME_VARIATION;

    // Build the audio graph
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.playbackRate.value = pitchShift;

    const panner = ctx.createStereoPanner();
    panner.pan.value = panValue;

    const gainNode = ctx.createGain();
    gainNode.gain.value = gainValue;

    source.connect(panner);
    panner.connect(gainNode);
    gainNode.connect(ctx.destination);

    source.start(0);
  }
}
